import React, { useCallback, useState, useMemo } from "react";
import { Button, Box } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useFormik } from "formik";
import * as yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  useGetChamberQuery,
  useUpdateChamberMutation,
  ChamberInput,
  useGetUserQuery,
} from "../../../../graphql/types";
import LogoPicker from "../../../common/utilities/LogoPicker";
import BannerPicker from "../../../common/utilities/BannerPicker";
import HoverToolTip from "../../../common/utilities/HoverToolTip";
import PhoneNumberFormat from "../../../common/utilities/PhoneNumberFormat";
import ProvincePicker from "../../../common/utilities/ProvincePicker";
import omitDeep from "omit-deep-lodash";
import { FormSection, InputField } from "../../../common/FormComponents";
import Loader from "../../../common/Loader";
import {
  WEBSITE_REGEX,
  VALIDATION_MESSAGES,
} from "../../../../utils/validationPatterns";

const CHARACTER_LIMIT_DESCRIPTION = 2000;

// Helper to handle formik errors
const getHelperText = (
  touched: boolean | undefined,
  error: any
): string | undefined => {
  return touched && error ? String(error) : undefined;
};

// Tooltip text for chamber member pro context
const tooltipTexts = {
  name: "Enter the official name of your Chamber/Board",
  description:
    "Provide a brief description of your chamber's mission and services",
  website: "Enter your chamber's official website URL",
  supportContactName:
    "Name of the primary contact person for support inquiries",
  supportContactEmail: "Email address where members can reach out for support",
  supportLink: "URL to your chamber's support or help page",
  phoneNumber: "Main contact phone number for your chamber",
  streetAddress: "Physical street address of your chamber",
  province: "Province or state where your chamber is located",
  city: "City where your chamber is located",
  postalCode: "Postal or zip code for your chamber's address",
  facebook: "Link to your chamber's Facebook page",
  instagram: "Link to your chamber's Instagram profile",
  threads: "Link to your chamber's Threads profile",
  linkedin: "Link to your chamber's LinkedIn page",
  twitter: "Link to your chamber's Twitter/X profile",
  tiktok: "Link to your chamber's TikTok profile",
  youtube: "Link to your chamber's YouTube channel",
  jobPostingLink: "Link to your job board or career opportunities page",
  eventsLink: "Link to your events calendar or upcoming events page",
  newsletterLink: "Link to your newsletter signup or archives",
  surveyLink: "Link to current surveys or feedback forms",
  advocacyWinsLink: "Link to a page highlighting your advocacy achievements",
  memberSignupLink: "Link to your membership application or signup page",
  logo: "Upload your chamber's official logo (recommended size: 400x400px)",
  sponsorLogo: "Upload a sponsor logo to display on your Chamber profile",
  sponsorLogoLink:
    "URL that users will be directed to when clicking the sponsor logo",
  profileBanner:
    "These banners will display on your chamber's profile page. Upload up to 4 image or video banners (16:9 recommended, min 1280x720)",
  directoryBanner:
    "These banners will display in the main member directory. Upload up to 4 image or video banners (16:9 recommended, min 1280x720)",
};

const ManageChamberProfile = () => {
  const [loader, setLoader] = useState(false);
  const [invalidError, setInvalidError] = useState(false);
  const [customError, setCustomError] = useState("");

  // Get current user to get chamber ID
  const { data: userData, loading: userLoading } = useGetUserQuery({
    variables: { userId: localStorage.getItem("userId") || "" },
  });

  const chamberId = userData?.getUser?.chamberId;

  // Get chamber data
  const { data: chamberData, loading: chamberLoading } = useGetChamberQuery({
    variables: { chamberId: chamberId || "" },
    skip: !chamberId,
  });

  const chamberInfo = chamberData?.getChamber;

  const [updateChamber] = useUpdateChamberMutation();

  // Validation schema
  const validationSchema = yup.object({
    name: yup.string().required("Chamber/Board name is required"),
    website: yup
      .string()
      .matches(WEBSITE_REGEX, VALIDATION_MESSAGES.WEBSITE_INVALID)
      .required("Website is required"),
    address: yup.object({
      province: yup.string().required("Province/State is required"),
      city: yup.string().required("City is required"),
      postalCode: yup.string().required("Postal/Zip code is required"),
      streetAddress: yup.string().required("Street address is required"),
    }),
    supportContactEmail: yup
      .string()
      .email("Please enter a valid email")
      .required("Support email is required"),

    jobPostingLink: yup
      .string()
      .matches(WEBSITE_REGEX, VALIDATION_MESSAGES.WEBSITE_INVALID),
    eventsLink: yup
      .string()
      .matches(WEBSITE_REGEX, VALIDATION_MESSAGES.WEBSITE_INVALID),
    newsletterLink: yup
      .string()
      .matches(WEBSITE_REGEX, VALIDATION_MESSAGES.WEBSITE_INVALID),
    surveyLink: yup
      .string()
      .matches(WEBSITE_REGEX, VALIDATION_MESSAGES.WEBSITE_INVALID),
    advocacyWinsLink: yup
      .string()
      .matches(WEBSITE_REGEX, VALIDATION_MESSAGES.WEBSITE_INVALID),
    memberSignupLink: yup
      .string()
      .matches(WEBSITE_REGEX, VALIDATION_MESSAGES.WEBSITE_INVALID),
    sponsorLogoLink: yup
      .string()
      .matches(WEBSITE_REGEX, VALIDATION_MESSAGES.WEBSITE_INVALID),
    facebook: yup
      .string()
      .matches(WEBSITE_REGEX, VALIDATION_MESSAGES.WEBSITE_INVALID),
    instagram: yup
      .string()
      .matches(WEBSITE_REGEX, VALIDATION_MESSAGES.WEBSITE_INVALID),
    threads: yup
      .string()
      .matches(WEBSITE_REGEX, VALIDATION_MESSAGES.WEBSITE_INVALID),
    linkedin: yup
      .string()
      .matches(WEBSITE_REGEX, VALIDATION_MESSAGES.WEBSITE_INVALID),
    twitter: yup
      .string()
      .matches(WEBSITE_REGEX, VALIDATION_MESSAGES.WEBSITE_INVALID),
    tiktok: yup
      .string()
      .matches(WEBSITE_REGEX, VALIDATION_MESSAGES.WEBSITE_INVALID),
    youtube: yup
      .string()
      .matches(WEBSITE_REGEX, VALIDATION_MESSAGES.WEBSITE_INVALID),
  });

  // Initial values
  const initialValuesMemo = useMemo(() => {
    const initialValues: ChamberInput = {
      id: chamberInfo?.id || "",
      name: chamberInfo?.name || "",
      description: chamberInfo?.description || "",
      website: chamberInfo?.website || "",
      supportContactEmail: chamberInfo?.supportContactEmail || "",
      supportContactName: chamberInfo?.supportContactName || "",
      phoneNumber: chamberInfo?.phoneNumber || "",
      facebook: chamberInfo?.facebook || "",
      instagram: chamberInfo?.instagram || "",
      threads: chamberInfo?.threads || "",
      linkedin: chamberInfo?.linkedin || "",
      twitter: chamberInfo?.twitter || "",
      tiktok: chamberInfo?.tiktok || "",
      youtube: chamberInfo?.youtube || "",
      jobPostingLink: chamberInfo?.jobPostingLink || "",
      address: {
        city: chamberInfo?.address.city || "",
        postalCode: chamberInfo?.address.postalCode || "",
        province: chamberInfo?.address.province || "",
        streetAddress: chamberInfo?.address.streetAddress || "",
      },
      eventsLink: chamberInfo?.eventsLink || "",
      newsletterLink: chamberInfo?.newsletterLink || "",
      surveyLink: chamberInfo?.surveyLink || "",
      advocacyWinsLink: chamberInfo?.advocacyWinsLink || "",
      memberSignupLink: chamberInfo?.memberSignupLink || "",
      logo: chamberInfo?.logo || undefined,
      sponsorLogo: chamberInfo?.sponsorLogo || undefined,
      sponsorLogoLink: chamberInfo?.sponsorLogoLink || undefined,
      directoryBanner: chamberInfo?.directoryBanner || undefined,
      profileBanner: chamberInfo?.profileBanner || undefined,
    };
    return omitDeep(initialValues, "__typename");
  }, [chamberInfo]) as unknown as ChamberInput;

  const formik = useFormik({
    initialValues: initialValuesMemo,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (input: ChamberInput) => {
      setLoader(true);
      try {
        await updateChamber({
          variables: {
            id: input.id,
            input: {
              address: {
                city: input.address.city,
                postalCode: input.address.postalCode,
                province: input.address.province,
                streetAddress: input.address.streetAddress,
              },
              eventsLink: input.eventsLink,
              jobPostingLink: input.jobPostingLink,
              newsletterLink: input.newsletterLink,
              surveyLink: input.surveyLink,
              advocacyWinsLink: input.advocacyWinsLink,
              memberSignupLink: input.memberSignupLink,
              name: input.name,
              description: input.description,
              supportContactEmail: input.supportContactEmail
                .trim()
                .toLowerCase(),
              supportContactName: input.supportContactName,
              phoneNumber: input.phoneNumber,
              facebook: input.facebook,
              instagram: input.instagram,
              threads: input.threads,
              linkedin: input.linkedin,
              twitter: input.twitter,
              tiktok: input.tiktok,
              youtube: input.youtube,
              website: input.website,
              logo: input.logo,
              sponsorLogo: input.sponsorLogo,
              sponsorLogoLink: input.sponsorLogoLink,
              id: input.id,
              directoryBanner: input.directoryBanner,
              profileBanner: input.profileBanner,
            },
          },
          refetchQueries: ["getUser", "getChamber"],
        });
        setLoader(false);
        toast.success("Updated successfully");
      } catch (error: any) {
        setInvalidError(true);
        setCustomError(error?.message || "An error occurred");
        setLoader(false);
        toast.error("Failed to update chamber profile");
      }
    },
  });

  // Base64 conversion helper
  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        resolve(fileReader.result as string);
      };
      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };

  const handleCreateBase64 = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const base64 = await convertToBase64(file);
      formik.setFieldValue("logo", base64);
    },
    [formik]
  );

  const deleteLogo = (e: React.MouseEvent) => {
    e.preventDefault();
    formik.setFieldValue("logo", undefined);
  };

  const handleCreateBase64SponsorLogo = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const base64 = await convertToBase64(file);
      formik.setFieldValue("sponsorLogo", base64);
    },
    [formik]
  );

  const deleteSponsorLogo = (e: React.MouseEvent) => {
    e.preventDefault();
    formik.setFieldValue("sponsorLogo", undefined);
    formik.setFieldValue("sponsorLogoLink", undefined);
  };

  // Show loading state while fetching user or chamber data
  if (userLoading || (chamberId && chamberLoading)) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Loader />
      </Box>
    );
  }

  // If no chamber ID is found after loading user data
  if (!userLoading && !chamberId) {
    return <></>;
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <Box className="max-w-4xl mx-auto">
        {/* Chamber Information Section */}
        <FormSection
          title="Chamber Information"
          tooltipText="Enter your chamber's basic information including name and description"
        >
          <Box className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <Box
              className="md:col-span-2"
              display="flex"
              alignItems="flex-start"
              gap={1}
            >
              <Box flexGrow={1}>
                <InputField
                  id="name"
                  name="name"
                  label="Chamber/Board Name"
                  value={formik.values.name || ""}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={getHelperText(
                    formik.touched.name,
                    formik.errors.name
                  )}
                  required
                />
              </Box>
              <HoverToolTip description={tooltipTexts.name} />
            </Box>

            <Box
              className="md:col-span-2"
              display="flex"
              alignItems="flex-start"
              gap={1}
            >
              <Box flexGrow={1}>
                <InputField
                  id="description"
                  name="description"
                  label="Chamber/Board Description"
                  value={formik.values.description || ""}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.description &&
                    Boolean(formik.errors.description)
                  }
                  helperText={getHelperText(
                    formik.touched.description,
                    formik.errors.description
                  )}
                  multiline
                  rows={3}
                  maxLength={CHARACTER_LIMIT_DESCRIPTION}
                />
              </Box>
              <HoverToolTip description={tooltipTexts.description} />
            </Box>
          </Box>
        </FormSection>

        {/* Contact Information Section */}
        <FormSection
          title="Contact Information"
          tooltipText="Provide essential contact details that members can use to reach your chamber for support and inquiries"
        >
          <Box className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box flexGrow={1}>
                <InputField
                  id="supportContactName"
                  name="supportContactName"
                  label="Support Contact Name"
                  value={formik.values.supportContactName || ""}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.supportContactName &&
                    Boolean(formik.errors.supportContactName)
                  }
                  helperText={getHelperText(
                    formik.touched.supportContactName,
                    formik.errors.supportContactName
                  )}
                  required
                />
              </Box>
              <HoverToolTip description={tooltipTexts.supportContactName} />
            </Box>

            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box flexGrow={1}>
                <InputField
                  id="supportContactEmail"
                  name="supportContactEmail"
                  label="Support Contact Email"
                  value={formik.values.supportContactEmail || ""}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.supportContactEmail &&
                    Boolean(formik.errors.supportContactEmail)
                  }
                  helperText={getHelperText(
                    formik.touched.supportContactEmail,
                    formik.errors.supportContactEmail
                  )}
                  required
                />
              </Box>
              <HoverToolTip description={tooltipTexts.supportContactEmail} />
            </Box>

            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box flexGrow={1}>
                <InputField
                  id="website"
                  name="website"
                  label="Website"
                  value={formik.values.website || ""}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.website && Boolean(formik.errors.website)
                  }
                  helperText={getHelperText(
                    formik.touched.website,
                    formik.errors.website
                  )}
                  required
                  placeholder="https://www.yourchamber.com"
                />
              </Box>
              <HoverToolTip description={tooltipTexts.website} />
            </Box>

            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box flexGrow={1}>
                <PhoneNumberFormat
                  formikErrors={formik.errors.phoneNumber}
                  formikVal={formik.values.phoneNumber || ""}
                  handleChange={formik.handleChange}
                  label="Phone Number"
                  error={
                    formik.touched.phoneNumber &&
                    Boolean(formik.errors.phoneNumber)
                  }
                  helperText={getHelperText(
                    formik.touched.phoneNumber,
                    formik.errors.phoneNumber
                  )}
                />
              </Box>
              <HoverToolTip description={tooltipTexts.phoneNumber} />
            </Box>
          </Box>
        </FormSection>

        {/* Address Section */}
        <FormSection
          title="Address"
          tooltipText="Enter your chamber's complete physical address for member visits and official correspondence"
        >
          <Box className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <Box
              className="md:col-span-2"
              display="flex"
              alignItems="flex-start"
              gap={1}
            >
              <Box flexGrow={1}>
                <InputField
                  id="address.streetAddress"
                  name="address.streetAddress"
                  label="Street Address"
                  value={formik.values.address?.streetAddress || ""}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.address?.streetAddress &&
                    Boolean(formik.errors.address?.streetAddress)
                  }
                  helperText={getHelperText(
                    formik.touched.address?.streetAddress,
                    formik.errors.address?.streetAddress
                  )}
                  required
                />
              </Box>
              <HoverToolTip description={tooltipTexts.streetAddress} />
            </Box>

            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box flexGrow={1}>
                <InputField
                  id="address.city"
                  name="address.city"
                  label="City"
                  value={formik.values.address?.city || ""}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.address?.city &&
                    Boolean(formik.errors.address?.city)
                  }
                  helperText={getHelperText(
                    formik.touched.address?.city,
                    formik.errors.address?.city
                  )}
                  required
                />
              </Box>
              <HoverToolTip description={tooltipTexts.city} />
            </Box>

            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box flexGrow={1}>
                <ProvincePicker
                  formikVal={formik.values.address?.province || ""}
                  formikErrors={
                    !!(
                      formik.touched.address?.province &&
                      Boolean(formik.errors.address?.province)
                    )
                  }
                  formikStringError={formik.errors.address?.province}
                  formikSetFieldValue={formik.setFieldValue}
                />
              </Box>
              <HoverToolTip description={tooltipTexts.province} />
            </Box>

            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box flexGrow={1}>
                <InputField
                  id="address.postalCode"
                  name="address.postalCode"
                  label="Postal/Zip Code"
                  value={formik.values.address?.postalCode || ""}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.address?.postalCode &&
                    Boolean(formik.errors.address?.postalCode)
                  }
                  helperText={getHelperText(
                    formik.touched.address?.postalCode,
                    formik.errors.address?.postalCode
                  )}
                  required
                />
              </Box>
              <HoverToolTip description={tooltipTexts.postalCode} />
            </Box>
          </Box>
        </FormSection>

        {/* External Links Section */}
        <FormSection
          title="My Chamber Links"
          tooltipText="Add links to key resources and services that provide value to your chamber members and community"
        >
          <Box className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box flexGrow={1}>
                <InputField
                  id="jobPostingLink"
                  name="jobPostingLink"
                  label="Job Posting Link"
                  value={formik.values.jobPostingLink || ""}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.jobPostingLink &&
                    Boolean(formik.errors.jobPostingLink)
                  }
                  helperText={getHelperText(
                    formik.touched.jobPostingLink,
                    formik.errors.jobPostingLink
                  )}
                  placeholder="https://www.yourchamber.com/jobs"
                />
              </Box>
              <HoverToolTip description={tooltipTexts.jobPostingLink} />
            </Box>

            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box flexGrow={1}>
                <InputField
                  id="eventsLink"
                  name="eventsLink"
                  label="Events Link"
                  value={formik.values.eventsLink || ""}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.eventsLink &&
                    Boolean(formik.errors.eventsLink)
                  }
                  helperText={getHelperText(
                    formik.touched.eventsLink,
                    formik.errors.eventsLink
                  )}
                  placeholder="https://www.yourchamber.com/events"
                />
              </Box>
              <HoverToolTip description={tooltipTexts.eventsLink} />
            </Box>

            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box flexGrow={1}>
                <InputField
                  id="newsletterLink"
                  name="newsletterLink"
                  label="Newsletter Link"
                  value={formik.values.newsletterLink || ""}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.newsletterLink &&
                    Boolean(formik.errors.newsletterLink)
                  }
                  helperText={getHelperText(
                    formik.touched.newsletterLink,
                    formik.errors.newsletterLink
                  )}
                  placeholder="https://www.yourchamber.com/newsletter"
                />
              </Box>
              <HoverToolTip description={tooltipTexts.newsletterLink} />
            </Box>

            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box flexGrow={1}>
                <InputField
                  id="surveyLink"
                  name="surveyLink"
                  label="Survey Link"
                  value={formik.values.surveyLink || ""}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.surveyLink &&
                    Boolean(formik.errors.surveyLink)
                  }
                  helperText={getHelperText(
                    formik.touched.surveyLink,
                    formik.errors.surveyLink
                  )}
                  placeholder="https://www.yourchamber.com/survey"
                />
              </Box>
              <HoverToolTip description={tooltipTexts.surveyLink} />
            </Box>

            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box flexGrow={1}>
                <InputField
                  id="advocacyWinsLink"
                  name="advocacyWinsLink"
                  label="Advocacy Wins Link"
                  value={formik.values.advocacyWinsLink || ""}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.advocacyWinsLink &&
                    Boolean(formik.errors.advocacyWinsLink)
                  }
                  helperText={getHelperText(
                    formik.touched.advocacyWinsLink,
                    formik.errors.advocacyWinsLink
                  )}
                  placeholder="https://www.yourchamber.com/advocacy"
                />
              </Box>
              <HoverToolTip description={tooltipTexts.advocacyWinsLink} />
            </Box>

            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box flexGrow={1}>
                <InputField
                  id="memberSignupLink"
                  name="memberSignupLink"
                  label="New Member Signup Link"
                  value={formik.values.memberSignupLink || ""}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.memberSignupLink &&
                    Boolean(formik.errors.memberSignupLink)
                  }
                  helperText={getHelperText(
                    formik.touched.memberSignupLink,
                    formik.errors.memberSignupLink
                  )}
                  placeholder="https://www.yourchamber.com/join"
                />
              </Box>
              <HoverToolTip description={tooltipTexts.memberSignupLink} />
            </Box>
          </Box>
        </FormSection>

        {/* Social Media Section */}
        <FormSection
          title="Social Media"
          tooltipText="Connect your chamber's social media accounts to increase visibility and member engagement"
        >
          <Box className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box flexGrow={1}>
                <InputField
                  id="facebook"
                  name="facebook"
                  label="Facebook Page URL"
                  value={formik.values.facebook || ""}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.facebook && Boolean(formik.errors.facebook)
                  }
                  helperText={getHelperText(
                    formik.touched.facebook,
                    formik.errors.facebook
                  )}
                  placeholder="https://www.facebook.com/yourchamber"
                />
              </Box>
              <HoverToolTip description={tooltipTexts.facebook} />
            </Box>

            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box flexGrow={1}>
                <InputField
                  id="instagram"
                  name="instagram"
                  label="Instagram Account URL"
                  value={formik.values.instagram || ""}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.instagram && Boolean(formik.errors.instagram)
                  }
                  helperText={getHelperText(
                    formik.touched.instagram,
                    formik.errors.instagram
                  )}
                  placeholder="https://www.instagram.com/yourchamber"
                />
              </Box>
              <HoverToolTip description={tooltipTexts.instagram} />
            </Box>

            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box flexGrow={1}>
                <InputField
                  id="threads"
                  name="threads"
                  label="Threads Account URL"
                  value={formik.values.threads || ""}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.threads && Boolean(formik.errors.threads)
                  }
                  helperText={getHelperText(
                    formik.touched.threads,
                    formik.errors.threads
                  )}
                  placeholder="https://www.threads.net/@yourchamber"
                />
              </Box>
              <HoverToolTip description={tooltipTexts.threads} />
            </Box>

            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box flexGrow={1}>
                <InputField
                  id="linkedin"
                  name="linkedin"
                  label="LinkedIn Page URL"
                  value={formik.values.linkedin || ""}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.linkedin && Boolean(formik.errors.linkedin)
                  }
                  helperText={getHelperText(
                    formik.touched.linkedin,
                    formik.errors.linkedin
                  )}
                  placeholder="https://www.linkedin.com/company/yourchamber"
                />
              </Box>
              <HoverToolTip description={tooltipTexts.linkedin} />
            </Box>

            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box flexGrow={1}>
                <InputField
                  id="twitter"
                  name="twitter"
                  label="Twitter/X Account URL"
                  value={formik.values.twitter || ""}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.twitter && Boolean(formik.errors.twitter)
                  }
                  helperText={getHelperText(
                    formik.touched.twitter,
                    formik.errors.twitter
                  )}
                  placeholder="https://twitter.com/yourchamber"
                />
              </Box>
              <HoverToolTip description={tooltipTexts.twitter} />
            </Box>

            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box flexGrow={1}>
                <InputField
                  id="tiktok"
                  name="tiktok"
                  label="TikTok Account URL"
                  value={formik.values.tiktok || ""}
                  onChange={formik.handleChange}
                  error={formik.touched.tiktok && Boolean(formik.errors.tiktok)}
                  helperText={getHelperText(
                    formik.touched.tiktok,
                    formik.errors.tiktok
                  )}
                  placeholder="https://www.tiktok.com/@yourchamber"
                />
              </Box>
              <HoverToolTip description={tooltipTexts.tiktok} />
            </Box>

            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box flexGrow={1}>
                <InputField
                  id="youtube"
                  name="youtube"
                  label="YouTube Channel URL"
                  value={formik.values.youtube || ""}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.youtube && Boolean(formik.errors.youtube)
                  }
                  helperText={getHelperText(
                    formik.touched.youtube,
                    formik.errors.youtube
                  )}
                  placeholder="https://www.youtube.com/c/yourchamber"
                />
              </Box>
              <HoverToolTip description={tooltipTexts.youtube} />
            </Box>
          </Box>
        </FormSection>

        {/* Logo Section */}
        <FormSection
          title="Chamber Logo"
          tooltipText="Upload your chamber's official logo that will represent your organization across the platform"
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            gap={1}
          >
            <Box display="flex" alignItems="flex-start" gap={1}>
              <LogoPicker
                logo={formik.values.logo}
                deleteLogo={deleteLogo}
                base64={handleCreateBase64}
              />
              <HoverToolTip description={tooltipTexts.logo} />
            </Box>
          </Box>
        </FormSection>

        {/* Sponsor Logo Section */}
        <FormSection
          title="Sponsor Logo"
          tooltipText="Feature a sponsor's logo on your chamber profile with a clickable link to drive traffic to their website"
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            gap={4}
          >
            <Box display="flex" alignItems="flex-start" gap={1}>
              <LogoPicker
                logo={formik.values.sponsorLogo}
                deleteLogo={deleteSponsorLogo}
                base64={handleCreateBase64SponsorLogo}
              />
              <HoverToolTip description={tooltipTexts.sponsorLogo} />
            </Box>

            {formik.values.sponsorLogo && (
              <Box
                display="flex"
                alignItems="flex-start"
                gap={1}
                width="100%"
                maxWidth="431px"
              >
                <Box flexGrow={1}>
                  <InputField
                    id="sponsorLogoLink"
                    name="sponsorLogoLink"
                    label="Sponsor Logo Link"
                    value={formik.values.sponsorLogoLink || ""}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.sponsorLogoLink &&
                      Boolean(formik.errors.sponsorLogoLink)
                    }
                    helperText={getHelperText(
                      formik.touched.sponsorLogoLink,
                      formik.errors.sponsorLogoLink
                    )}
                    placeholder="https://www.sponsor.com"
                  />
                </Box>
                <HoverToolTip description={tooltipTexts.sponsorLogoLink} />
              </Box>
            )}
          </Box>
        </FormSection>

        {/* Banners Section */}
        <FormSection
          title="Chamber Profile Banners"
          tooltipText="Upload eye-catching banners that will be displayed prominently on your chamber's profile page"
        >
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="center"
            gap={1}
          >
            <Box flexGrow={1} display="flex" justifyContent="center">
              <BannerPicker
                formikVal={(formik.values.profileBanner as any) || []}
                formikSetFieldValue={(banners) =>
                  formik.setFieldValue("profileBanner", banners)
                }
                pickerLabel=""
              />
            </Box>
            <HoverToolTip description={tooltipTexts.profileBanner} />
          </Box>
        </FormSection>

        <FormSection
          title="Main Directory Banners"
          tooltipText="Upload banners that will be featured in the main member directory to promote your chamber to visitors"
        >
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="center"
            gap={1}
          >
            <Box flexGrow={1} display="flex" justifyContent="center">
              <BannerPicker
                formikVal={(formik.values.directoryBanner as any) || []}
                formikSetFieldValue={(banners) =>
                  formik.setFieldValue("directoryBanner", banners)
                }
                pickerLabel=""
              />
            </Box>
            <HoverToolTip description={tooltipTexts.directoryBanner} />
          </Box>
        </FormSection>

        {/* Error Display */}
        {invalidError && (
          <Box className="text-center text-red-500 my-4">
            {customError ||
              "An error occurred while updating the chamber profile"}
          </Box>
        )}

        {/* Validation Errors Display */}
        {Object.keys(formik.errors).length > 0 && formik.submitCount > 0 && (
          <Box className="text-center text-red-500 my-4 font-medium">
            Please fix all errors highlighted in red above to update
          </Box>
        )}

        {/* Submit Button */}
        <Box className="mt-8 mb-4 flex justify-center">
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loader}
            startIcon={loader ? null : <SaveIcon />}
            sx={{
              backgroundColor: "#E1A731",
              color: "white",
              fontSize: "1.5rem",
              fontWeight: "bold",
              borderRadius: "10px",
              minWidth: "200px",
              "&:hover": {
                backgroundColor: "#c78f1e",
              },
              "&:disabled": {
                backgroundColor: "#ccc",
              },
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              px: 4,
              py: 1.5,
            }}
          >
            {loader ? <Loader isWhite /> : "Update"}
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default ManageChamberProfile;
